<template>
	<div class="plot-container rounded-md overflow-hidden border border-border bg-muted-background">
		<div v-show="staticPlot == false" class="w-full h-full p-3">
			<div ref="plot" class="w-full h-full"></div>
		</div>
		<img ref="plotImage" class="plotly-static-graph w-full" v-show="staticPlot" :src="plotImageSrc" :width="plotWidth" :height="plotHeight" />
	</div>
</template>

<script setup>
import Plotly from 'plotly.js-dist'
import { ref, defineProps, defineEmits, onMounted, watch, computed } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { useColorMode } from '@vueuse/core'

const props = defineProps({ 
	data: Object,
	layout: Object,
	fixedRange: {
		type: Boolean,
		default: false
	},
	staticPlot: { 
		type: Boolean,
		default: false
	},
	plotWidth: {
		type: Number,
		default: 600, // Задайте розмір ширини за замовчуванням
	},
	plotHeight: {
		type: Number,
		default: 400, // Задайте розмір висоти за замовчуванням
	},
	theme: {
		type: String,
		default: "auto"
	}
})

const emit = defineEmits(['imageReady'])

const plot = ref(null)
const plotImage = ref(null)
const plotImageSrc = ref('')

const colorMode = useColorMode()

const theme = computed(() => {
	return props.theme == "auto" ? colorMode.value : props.theme
})

const data = computed(() => {
	if (props.data == null) return null

	const colors = {
		"primary": theme.value == 'dark' ? '#ddd' : '#333',
		"secondary": theme.value == 'dark' ? '#ddd2' : '#3332',
		"background": theme.value == 'dark' ? '#20202050' : '#bbb4'
	}

	// Create new data and replace color codes to actual data
	// Original data must not change
	const result = props.data.map(item => {
		if (item.marker?.color == null) return { ...item }

		const newColor = Object.keys(colors).includes(item.marker.color) ? 
			colors[item.marker.color] : 
			item.marker.color

		return {
			...item,
			marker: {
				...item.marker,
				color: newColor,
			},
		}
	})

	return result
})

const plotColors = computed(() => {
	const colors = {
		dark: { plot: "#171717", paper: "#262626" },
		light: { plot: "#fff", paper: "#e5e5e5" },
		export_dark: { plot: "#101010", paper: "#171717" },
		export_light: { plot: "#fff", paper: "#fafafa" },
	}

	return {
		plot: colors[`${props.staticPlot ? 'export_' : ''}${theme.value}`].plot,
		paper: colors[`${props.staticPlot ? 'export_' : ''}${theme.value}`].paper
	}
})

const defaultLayout = computed(() => {
	return {
		autosize: true,
		margin: { t: 10, b: 18, l: 52, r: 5, pad: 0 },
		plot_bgcolor: plotColors.value.plot,
		paper_bgcolor: plotColors.value.paper,
		font: { 
			color: (theme.value == 'dark' ? "white" : "#202020"), 
			family: '"Consolas", monospace' 
		},
		showlegend: false,
		title: null
	}
})

const layout = computed(() => {
	let finalLayout = defaultLayout.value

	// Merge with default settings
	if (props.layout != null) {
		finalLayout = {...finalLayout, ...props.layout}
	}

	if (props.fixedRange) {
		// Create objects if null
		if (finalLayout.xaxis == null) finalLayout.xaxis = {}
		if (finalLayout.yaxis == null) finalLayout.xaxis = {}

		// Set fixed to both axes
		finalLayout.xaxis.fixedrange = true
		finalLayout.yaxis.fixedrange = true
	}

	// finalLayout.xaxis = {
	// 	...finalLayout.xaxis,
	// 	gridcolor: theme.value == 'dark' ? '#1112' : '#aaa2',
	// }

	// finalLayout.yaxis = {
	// 	...finalLayout.yaxis,
	// 	gridcolor: theme.value == 'dark' ? '#111' : '#aaa',
	// }

	return finalLayout
})

const settings = {
	displaylogo: false,
	displayModeBar: false
}

// Plotly animation
// const animation = {
// 	transition: {
// 		duration: 500,
// 		easing: 'cubic-in-out'
// 	},
// 	frame: {
// 		duration: 500
// 	}
// }

function renderStatic() {
	console.log("Rendering static chart")

	const scale = 1.8

	Plotly.toImage(plot.value, {
		format: 'png',
		width: props.plotWidth / scale,
		height: props.plotHeight / scale,
		scale: scale
	}).then((dataUrl) => {
		plotImageSrc.value = dataUrl
		emit('imageReady', dataUrl)
	})
}

function renderChart() {
	Plotly.setPlotConfig({ locale: 'uk' });
	Plotly.newPlot(plot.value, data.value, layout.value, settings);
	if (props.staticPlot) renderStatic()
}

function updateChart() {
	Plotly.setPlotConfig({ locale: 'uk' });
	Plotly.react(plot.value, data.value, layout.value, settings);
	if (props.staticPlot) renderStatic()
}

onMounted(() => {
	renderChart()
})

watch([props, colorMode, defaultLayout], () => {
	updateChart()
})

useResizeObserver(plot, () => {
	updateChart()
})
</script>

<style scoped>

</style>